import { BadgeCheck, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Section } from '@/components/common/Blocks'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { toastError } from '../../ui'
import { usePersonalSettings, usePersonalWallets, useSaveSettings } from '../api'
import type { PersonalSettings } from '../types'

/** Maaş ve firma ödemelerinin kişisel gelirlere kilitli kayıt olarak aktarılması (kişinin tercihi). */
export function SalarySyncCard() {
  const settings = usePersonalSettings()
  if (!settings.data) return null
  return <SalarySyncForm key={settings.data.salaryWalletId + settings.data.syncFrom + String(settings.data.autoSalary)} initial={settings.data} />
}

function SalarySyncForm({ initial }: { initial: PersonalSettings }) {
  const { t } = useTranslation()
  const wallets = usePersonalWallets()
  const save = useSaveSettings()
  const [autoSalary, setAutoSalary] = useState(initial.autoSalary)
  const [walletId, setWalletId] = useState(initial.salaryWalletId || wallets.data?.[0]?.id || '')
  const [syncFrom, setSyncFrom] = useState(initial.syncFrom)
  const dirty = autoSalary !== initial.autoSalary || walletId !== initial.salaryWalletId || syncFrom !== initial.syncFrom

  const onSave = () =>
    save.mutate(
      { autoSalary, salaryWalletId: autoSalary ? walletId : '', syncFrom },
      { onSuccess: () => toast.success(t('finance.personal.salary.saved')), onError: toastError },
    )

  return (
    <Section title={t('finance.personal.salary.title')} icon={BadgeCheck}>
      {!initial.employeeLinked ? (
        <p className="text-sm text-muted-foreground">{t('finance.personal.salary.noEmployee')}</p>
      ) : (
        <div className="space-y-3">
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-0.5 size-4 accent-primary" checked={autoSalary} onChange={(e) => setAutoSalary(e.target.checked)} />
            <span>
              <span className="font-medium">{t('finance.personal.salary.toggle')}</span>
              <span className="block text-muted-foreground">{t('finance.personal.salary.hint')}</span>
            </span>
          </label>
          {autoSalary ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="salary-wallet">{t('finance.personal.salary.wallet')}</Label>
                <Select
                  id="salary-wallet"
                  value={walletId}
                  onChange={setWalletId}
                  placeholder={t('finance.personal.form.walletRequired')}
                  options={(wallets.data ?? []).map((w) => ({ value: w.id, label: w.name }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="salary-from">{t('finance.personal.salary.syncFrom')}</Label>
                <DateField id="salary-from" value={syncFrom} onChange={setSyncFrom} />
              </div>
            </div>
          ) : null}
          <div className="flex justify-end">
            <Button size="sm" onClick={onSave} disabled={!dirty || save.isPending || (autoSalary && !walletId)}>
              {save.isPending ? <Loader2 className="animate-spin" /> : null}
              {t('common.save')}
            </Button>
          </div>
        </div>
      )}
    </Section>
  )
}
